import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { History as HistoryIcon, Trophy, Download, Lock, Eye, Ticket, Users, DollarSign, Loader2 } from "lucide-react";
import { padNum, money, statusLabel, statusColor } from "@/lib/format";
import { toast } from "sonner";

const placeIcon = (p) => (p === 1 ? "🥇" : p === 2 ? "🥈" : p === 3 ? "🥉" : `${p}º`);

function Winners({ winners, winning_number }) {
  const list = winners?.length ? winners : (winning_number != null ? [{ place: 1, number: winning_number }] : []);
  if (!list.length) return <span className="text-neutral-400 text-sm">—</span>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {list.map((w) => (
        <span key={w.place} className="inline-flex items-center gap-1 text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-200 rounded-full px-2 py-0.5">
          {placeIcon(w.place)} #{padNum(w.number)}
        </span>
      ))}
    </div>
  );
}

export default function History() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = !!user?.is_super_admin;
  const isFull = isAdmin || user?.plan === "full";
  const [raffles, setRaffles] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [salesOpen, setSalesOpen] = useState(false);
  const [salesData, setSalesData] = useState(null);
  const [salesLoading, setSalesLoading] = useState(false);
  const [activeRaffle, setActiveRaffle] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        if (isAdmin) {
          const res = await api.get("/admin/history");
          setRaffles(res.data.raffles); setTotals(res.data.totals);
        } else {
          const res = await api.get("/history/raffles");
          setRaffles(res.data);
        }
      } catch (e) { toast.error("No se pudo cargar el historial"); }
      finally { setLoading(false); }
    })();
  }, [isAdmin]);

  const openSales = async (r) => {
    setActiveRaffle(r); setSalesOpen(true); setSalesLoading(true); setSalesData(null);
    try {
      const res = await api.get(`/history/raffles/${r.id}/sales`);
      setSalesData(res.data);
    } catch (e) { toast.error("No se pudieron cargar las ventas"); }
    finally { setSalesLoading(false); }
  };

  const downloadCsv = async (r) => {
    try {
      const res = await api.get(`/history/raffles/${r.id}/sales/export`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url; a.download = `ventas_${r.seed_code || r.id}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("No se pudo descargar el CSV"); }
  };

  return (
    <DashboardLayout title="Historial">
      <div className="flex items-center gap-2 mb-5">
        <div className="h-9 w-9 rounded-xl bg-neutral-900 text-white flex items-center justify-center"><HistoryIcon size={18} /></div>
        <p className="text-sm text-neutral-500">{isAdmin ? "Historial general de todas las rifas de la plataforma." : "Tus rifas cerradas y sorteadas, con ventas y ganadores."}</p>
      </div>

      {isAdmin && totals && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="history-totals">
          {[
            { label: "Rifas", value: totals.raffles, icon: Ticket },
            { label: "Números vendidos", value: totals.numbers_sold, icon: Trophy },
            { label: "Compradores", value: totals.buyers, icon: Users },
            { label: "Recaudado (aprob.)", value: money(totals.revenue), icon: DollarSign },
          ].map((c) => {
            const Icon = c.icon;
            return (
              <div key={c.label} className="bg-white border border-neutral-200 rounded-2xl p-4">
                <div className="h-9 w-9 rounded-xl bg-[#E6F7FE] text-[#00AEEF] flex items-center justify-center mb-2"><Icon size={18} /></div>
                <p className="font-display font-extrabold text-2xl">{c.value}</p>
                <p className="text-xs text-neutral-500">{c.label}</p>
              </div>
            );
          })}
        </div>
      )}

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-neutral-400" /></div>
      ) : raffles.length === 0 ? (
        <div className="py-16 text-center text-neutral-500" data-testid="history-empty">
          <HistoryIcon size={40} className="mx-auto mb-3 text-neutral-300" />
          Aún no hay rifas cerradas o sorteadas en tu historial.
        </div>
      ) : (
        <div className="bg-white border border-neutral-200 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="history-table">
              <thead className="bg-neutral-50 text-neutral-500 text-xs uppercase tracking-wide">
                <tr>
                  {isAdmin && <th className="text-left px-4 py-3">Organizador</th>}
                  <th className="text-left px-4 py-3">Rifa</th>
                  <th className="text-left px-4 py-3">Estado</th>
                  <th className="text-right px-4 py-3">Vendidos</th>
                  <th className="text-right px-4 py-3">Recaudado</th>
                  <th className="text-left px-4 py-3">Ganadores</th>
                  <th className="text-right px-4 py-3">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {raffles.map((r) => (
                  <tr key={r.id} data-testid={`history-row-${r.id}`} className="hover:bg-neutral-50">
                    {isAdmin && (
                      <td className="px-4 py-3">
                        <p className="font-medium text-neutral-800">{r.organizer_name}</p>
                        <p className="text-xs text-neutral-400">{r.organizer_email}</p>
                      </td>
                    )}
                    <td className="px-4 py-3 font-medium text-neutral-800">{r.title}</td>
                    <td className="px-4 py-3"><span className={`text-xs font-semibold px-2 py-1 rounded-full ${statusColor(r.status)}`}>{statusLabel(r.status)}</span></td>
                    <td className="px-4 py-3 text-right">{r.sold}/{r.total_numbers || "—"}</td>
                    <td className="px-4 py-3 text-right">{money(r.revenue, r.currency)}</td>
                    <td className="px-4 py-3"><Winners winners={r.winners} winning_number={r.winning_number} /></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <Button size="sm" variant="outline" data-testid={`view-sales-${r.id}`} onClick={() => openSales(r)} className="rounded-lg gap-1"><Eye size={14} /> Ventas</Button>
                        {isFull ? (
                          <Button size="sm" variant="outline" data-testid={`csv-sales-${r.id}`} onClick={() => downloadCsv(r)} className="rounded-lg gap-1 text-[#0092CC] border-[#B3E7FB]"><Download size={14} /> CSV</Button>
                        ) : (
                          <Button size="sm" variant="outline" data-testid={`csv-sales-locked-${r.id}`} onClick={() => { toast.info("Exportar a CSV es parte del Acceso Completo."); navigate("/dashboard"); }} className="rounded-lg gap-1 text-neutral-400 border-dashed"><Lock size={13} /> CSV</Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={salesOpen} onOpenChange={setSalesOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Histórico de ventas — {activeRaffle?.title}</DialogTitle></DialogHeader>
          {salesLoading ? (
            <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-neutral-400" /></div>
          ) : salesData ? (
            <div>
              {salesData.winners?.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-amber-600 mb-2 flex items-center gap-1"><Trophy size={14} /> Ganadores</p>
                  <div className="space-y-1.5">
                    {salesData.winners.map((w) => (
                      <div key={w.place} className="flex items-center gap-2 text-sm bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
                        <span className="font-bold">{placeIcon(w.place)} #{padNum(w.number)}</span>
                        <span className="text-neutral-500">{w.buyer_name || "—"}</span>
                        {w.buyer_phone && <span className="text-neutral-400 ml-auto text-xs">{w.buyer_phone}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-2">Compradores aprobados ({salesData.sales.length})</p>
              {salesData.sales.length === 0 ? (
                <p className="text-sm text-neutral-400 py-4">No hubo ventas aprobadas.</p>
              ) : (
                <div className="max-h-[45vh] overflow-y-auto divide-y divide-neutral-100 border border-neutral-200 rounded-xl">
                  {salesData.sales.map((s, i) => (
                    <div key={i} data-testid={`sale-row-${i}`} className="px-3 py-2.5 flex items-start gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-neutral-800 truncate">{s.buyer_name}</p>
                        <p className="text-xs text-neutral-400 truncate">{s.buyer_email} · {s.buyer_phone}</p>
                      </div>
                      <div className="text-right">
                        <div className="flex flex-wrap gap-1 justify-end max-w-[220px]">
                          {s.numbers.map((n) => <span key={n} className="text-[11px] font-semibold bg-neutral-100 rounded px-1.5 py-0.5">#{padNum(n)}</span>)}
                        </div>
                        <p className="text-xs text-neutral-500 mt-1">{money(s.amount, salesData.currency)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
